import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { intakeApi } from '../api/api';
import { Submission, Division, Products } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import './SubmissionDetail.css';

interface OrderDetails {
  divisions?: Division[];
  products?: Products;
}

const SubmissionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchSubmission = async (): Promise<void> => {
      try {
        const response = await intakeApi.get(id || '');
        setSubmission(response.data.submission);
      } catch (err) {
        const error = err as { response?: { data?: { message?: string } } };
        setError(error.response?.data?.message || 'Failed to load submission');
      }
      setLoading(false);
    };

    fetchSubmission();
  }, [id]);

  const handleDelete = async (): Promise<void> => {
    if (!window.confirm('Are you sure you want to cancel this submission?')) {
      return;
    }

    setDeleting(true);
    try {
      await intakeApi.delete(id || '');
      navigate('/dashboard', { state: { message: 'Submission cancelled' } });
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      setError(error.response?.data?.message || 'Failed to cancel submission');
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusClass = (status: string): string => `status-badge status-${status}`;

  if (loading) {
    return (
      <div className="submission-detail">
        <div className="loading-container">
          <LoadingSpinner text="Loading submission..." />
        </div>
      </div>
    );
  }

  if (error || !submission) {
    return (
      <div className="submission-detail">
        <div className="alert alert-error">{error || 'Submission not found'}</div>
        <Link to="/dashboard" className="btn btn-secondary">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  const canEdit = submission.status === 'submitted';
  const orderDetails: OrderDetails = submission.order_details || {};

  return (
    <div className="submission-detail">
      <div className="detail-header">
        <div>
          <Link to="/dashboard" className="back-link">&larr; Back to Dashboard</Link>
          <h1 className="page-title">{submission.submission_number}</h1>
          <span className={getStatusClass(submission.status)}>
            {submission.status}
          </span>
        </div>
        {canEdit && (
          <div className="detail-actions">
            <Link to={`/intake/${id}/edit`} className="btn btn-primary">
              Edit Submission
            </Link>
            <button
              onClick={handleDelete}
              className="btn btn-danger"
              disabled={deleting}
            >
              {deleting ? <LoadingSpinner size="small" /> : 'Cancel Submission'}
            </button>
          </div>
        )}
      </div>

      <div className="detail-sections">
        <div className="detail-section">
          <h2 className="section-title">Contact Information</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>League Name</label>
              <p>{submission.league_name}</p>
            </div>
            <div className="detail-item">
              <label>Contact Name</label>
              <p>{submission.contact_name}</p>
            </div>
            <div className="detail-item">
              <label>Email</label>
              <p>{submission.contact_email}</p>
            </div>
            <div className="detail-item">
              <label>Phone</label>
              <p>{submission.contact_phone || '-'}</p>
            </div>
          </div>
        </div>

        <div className="detail-section">
          <h2 className="section-title">Order Information</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Season</label>
              <p>{submission.season || '-'}</p>
            </div>
            <div className="detail-item">
              <label>Requested Delivery Date</label>
              <p>{formatDate(submission.requested_date)}</p>
            </div>
            <div className="detail-item">
              <label>Total Teams</label>
              <p>{submission.total_teams}</p>
            </div>
            <div className="detail-item">
              <label>Total Items</label>
              <p>{submission.total_items}</p>
            </div>
          </div>
          {submission.notes && (
            <div className="detail-item full-width">
              <label>Notes</label>
              <p className="notes-text">{submission.notes}</p>
            </div>
          )}
        </div>

        {orderDetails.divisions && orderDetails.divisions.length > 0 && (
          <div className="detail-section">
            <h2 className="section-title">Divisions & Teams</h2>
            {orderDetails.divisions.map((division, divIndex) => (
              <div key={divIndex} className="division-card">
                <h3 className="division-name">{division.name}</h3>
                <div className="teams-list">
                  {division.teams && division.teams.map((team, teamIndex) => (
                    <div key={teamIndex} className="team-item">
                      <strong>{team.name}</strong>
                      {team.color && <span>Color: {team.color}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {orderDetails.products && (
          <div className="detail-section">
            <h2 className="section-title">Products Requested</h2>
            <div className="products-summary">
              {orderDetails.products.jerseys && (
                <div className="product-item">
                  <h4>Jerseys</h4>
                  <p>Type: {orderDetails.products.jerseys.type || 'TBD'}</p>
                  {orderDetails.products.jerseys.notes && (
                    <p className="product-notes">{orderDetails.products.jerseys.notes}</p>
                  )}
                </div>
              )}
              {orderDetails.products.pants && (
                <div className="product-item">
                  <h4>Pants</h4>
                  <p>Type: {orderDetails.products.pants.type || 'TBD'}</p>
                </div>
              )}
              {orderDetails.products.belts && (
                <div className="product-item">
                  <h4>Belts</h4>
                </div>
              )}
              {orderDetails.products.socks && (
                <div className="product-item">
                  <h4>Socks</h4>
                  <p>Type: {orderDetails.products.socks.type || 'TBD'}</p>
                </div>
              )}
              {orderDetails.products.caps && (
                <div className="product-item">
                  <h4>Caps</h4>
                  <p>Type: {orderDetails.products.caps.type || 'TBD'}</p>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="detail-section">
          <h2 className="section-title">Submission Timeline</h2>
          <div className="timeline">
            <div className="timeline-item">
              <div className="timeline-dot active"></div>
              <div className="timeline-content">
                <strong>Submitted</strong>
                <p>{formatDate(submission.created_at)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmissionDetail;
